import { useEffect } from "react";
import { getEndUserDetails } from "../../utils/fetchUtils";
import {
  isTelemetryEnabled,
  resetTelemetryIdentity,
  setTelemetryEndUser,
  setTelemetryOrganization,
} from "../telemetry";

interface Params {
  linkToken: string | undefined;
  apiBaseURL: string | undefined;
}

/**
 * Attaches identity to this Link session's telemetry: the end user as a person,
 * the Merge customer as a group. Lets a funnel be filtered to one customer, and
 * a stuck session be traced back to one end user.
 *
 * Gated on the telemetry kill switch, so these identifiers are never even
 * requested for a browser with telemetry off. The gate is the kill switch rather
 * than the plugin having loaded: the plugin can fail to load for reasons that
 * have nothing to do with consent (ad blocker, network policy), and both calls
 * buffer until it arrives.
 */
const useTelemetryIdentity = ({ linkToken, apiBaseURL }: Params): void => {
  // Resolve the end user behind the current link token and identify the session.
  useEffect(() => {
    if (linkToken == null || !apiBaseURL || !isTelemetryEnabled()) {
      return undefined;
    }

    // The iframe is reused across sessions and can be handed a new link token
    // belonging to a different end user at a different customer. Forget the
    // previous one first, or the two share a person, a group and a session.
    resetTelemetryIdentity();

    let cancelled = false;

    getEndUserDetails({
      apiBaseURL,
      linkToken,
      onResponse: (endUser) => {
        // The token changed while this was in flight; applying it now would
        // attach the previous end user to the new session.
        if (cancelled) {
          return;
        }
        // Guarded independently: a response missing one identifier should not
        // discard the other.
        if (endUser?.id) {
          setTelemetryEndUser(endUser.id);
        }
        if (endUser?.customer_organization_id) {
          setTelemetryOrganization(
            endUser.customer_organization_id,
            endUser.customer_organization_name
          );
        }
      },
      // Best-effort: telemetry stays anonymous rather than the flow failing.
      // Also covers a backend that predates this endpoint.
      onError: () => {},
    });

    return () => {
      cancelled = true;
    };
  }, [linkToken, apiBaseURL]);
};

export default useTelemetryIdentity;
